import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, Navigate } from "react-router-dom";
import { Eye, EyeOff, LogIn, Moon, Sun } from "lucide-react";
import LogoGKPY from "../components/LogoGKPY";
// Satu sumber asset foto gedung GKPY Jatinegara (hanya untuk panel kiri Login)
const GEDUNG_GKPY_SRC = `${process.env.PUBLIC_URL || ""}/assets/gedung-gkpy.webp`;
import { useAuth } from "../lib/auth";
import { errMsg } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

export default function LoginPage() {
  const { login, user, ready } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [dark, setDark] = useState(() => localStorage.getItem("siap_gkpy_login_dark") === "1");
  useEffect(() => { localStorage.setItem("siap_gkpy_login_dark", dark ? "1" : "0"); }, [dark]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const expired = new URLSearchParams(location.search).get("expired");

  if (ready && user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    if (!identifier.trim() || !password) { setError("Username/email dan password wajib diisi"); return; }
    setLoading(true); setError("");
    try {
      await login(identifier.trim(), password);
      navigate(location.state?.from || "/", { replace: true });
    } catch (err) {
      setError(errMsg(err, "Login gagal"));
    } finally { setLoading(false); }
  };

  return (
    <div className={`min-h-screen grid lg:grid-cols-2 ${dark ? "bg-slate-900" : "bg-[hsl(224,64%,33%)]"}`}>
      <div className="hidden lg:flex relative flex-col justify-between p-14 text-white overflow-hidden login-bg" style={{ backgroundImage: `url(${GEDUNG_GKPY_SRC})` }} data-testid="login-left-panel">
        <div className="absolute inset-0 login-overlay" data-testid="login-overlay" aria-hidden="true" />
        <div className="relative z-10" aria-hidden="true" />
        <div className="relative z-10 space-y-4 max-w-md" data-testid="login-branding">
          <h1 className="text-5xl xl:text-6xl font-bold leading-tight tracking-tight drop-shadow" data-testid="brand-title">SIAP GKPY</h1>
          <p className="text-lg xl:text-xl font-semibold text-white/95" data-testid="brand-subtitle">Sistem Informasi Administrasi & Pelayanan</p>
          <p className="text-white/80 text-sm xl:text-base" data-testid="brand-desc">Kelola data jemaat, keluarga, dan pelayanan gereja dengan aman dan terintegrasi.</p>
        </div>
        <p className="relative z-10 text-xs text-white/70" data-testid="brand-copyright">© GKPY. Akses terbatas untuk petugas yang berwenang.</p>
      </div>

      <div className={`relative flex items-center justify-center p-5 sm:p-10 lg:rounded-l-[2.5rem] ${dark ? "bg-slate-950 text-slate-100" : "bg-slate-50"}`}>
        <button type="button" onClick={() => setDark((d) => !d)} aria-label="Ganti mode gelap/terang" data-testid="dark-mode-toggle"
          className={`absolute top-4 right-4 h-11 w-11 rounded-full border flex items-center justify-center transition-colors ${dark ? "border-slate-700 bg-slate-900 text-amber-300" : "border-slate-200 bg-white text-slate-600 hover:bg-slate-100"}`}>
          {dark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
        </button>
        <form onSubmit={submit} className="w-full max-w-sm space-y-6" data-testid="login-form">
          <div className="flex flex-col items-center text-center gap-3 mb-2" data-testid="login-brand">
            <LogoGKPY className="h-20 sm:h-24" testId="logo-gkpy-login" />
            <div><div className={`font-bold text-lg ${dark ? "text-white" : "text-slate-900"}`}>SIAP GKPY</div><div className={`text-xs ${dark ? "text-slate-400" : "text-slate-500"}`}>Sistem Informasi Administrasi & Pelayanan</div></div>
          </div>
          <div>
            <h2 className={`text-2xl font-bold tracking-tight ${dark ? "text-white" : "text-slate-900"}`}>Masuk</h2>
            <p className={`text-sm mt-1 ${dark ? "text-slate-400" : "text-slate-500"}`}>Gunakan akun yang diberikan oleh Admin.</p>
          </div>
          {expired && !error && <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2" data-testid="session-expired-msg">Sesi Anda berakhir. Silakan login kembali.</p>}
          <div className="space-y-4">
            <div>
              <Label className={`text-sm font-medium mb-1.5 block ${dark ? "text-slate-300" : "text-slate-700"}`}>Username atau Email</Label>
              <Input data-testid="login-identifier" autoComplete="username" value={identifier} onChange={(e) => setIdentifier(e.target.value)} className={`min-h-[48px] rounded-xl ${dark ? "bg-slate-900 border-slate-700 text-white" : "bg-white"}`} placeholder="admin atau nama@email.com" />
            </div>
            <div>
              <Label className={`text-sm font-medium mb-1.5 block ${dark ? "text-slate-300" : "text-slate-700"}`}>Password</Label>
              <div className="relative">
                <Input data-testid="login-password" type={show ? "text" : "password"} autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} className={`min-h-[48px] rounded-xl pr-12 ${dark ? "bg-slate-900 border-slate-700 text-white" : "bg-white"}`} placeholder="••••••••" />
                <button type="button" onClick={() => setShow((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" data-testid="toggle-password-btn" aria-label="Tampilkan password">
                  {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
          </div>
          {error && <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-3 py-2" data-testid="login-error">{error}</p>}
          <Button type="submit" disabled={loading} className="w-full min-h-[48px] rounded-xl bg-[hsl(224,64%,33%)] hover:bg-[hsl(224,64%,28%)] text-base font-semibold" data-testid="login-submit-btn">
            <LogIn className="h-4 w-4 mr-2" /> {loading ? "Memproses..." : "Masuk"}
          </Button>
        </form>
      </div>
    </div>
  );
}
